/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * MaterialTable
 * -----------------------------------------------------------------------------
 * Hardened / memoised rewrite of the custom Material React Table wrapper.
 *
 * Key engineering decisions (see PR notes for the full rationale):
 *  - Zero work in render: no canvas text measurement, no per-render column
 *    rebuild, no derived-state duplication of the `columns` prop.
 *  - Every callback prop is wrapped in `useEventCallback` so that the memoised
 *    column defs / table options never invalidate because a parent forgot to
 *    memoise an inline arrow function.
 *  - Inline-edit state (validation errors + schema) travels through context, so
 *    typing / validating never invalidates the column definitions.
 *  - All persistence side effects (`updatePageInfo`) are edge triggered.
 * -----------------------------------------------------------------------------
 */

import React, {
	cloneElement,
	createContext,
	memo,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import {
	MRT_Cell,
	MRT_ColumnDef,
	MRT_ColumnPinningState,
	MRT_ColumnSizingInfoState,
	MRT_ColumnSizingState,
	MRT_Header,
	MRT_Row,
	MRT_RowData,
	MRT_RowSelectionState,
	MRT_SortingState,
	MRT_TableInstance,
	MRT_TableState,
	MaterialReactTable,
	MaterialReactTableProps,
	useMaterialReactTable
} from 'material-react-table';
import {
	Box,
	Divider,
	IconButton,
	ListItemIcon,
	ListItemText,
	MenuItem,
	SvgIconProps,
	styled
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Add from '@mui/icons-material/Add';
import dayjs from 'dayjs';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageState, usePages } from '@erpsquad/common/runtime';

import { IPaginationModel } from '../../@types/pagination-model';
import { getApiConfigSafe } from '../../utils/api-config';
import Menu from '../menu/menu';
import Typography from '../typography/typography';
import SearchBar from '../search-bar/search-bar';
import images from '../../assets/images';
import {
	ArrowUpDown,
	CheckBoxIcon,
	Close,
	Edit,
	RecgtangleIcon,
	Save,
	Trash
} from '../icons';
import InlineEditFields from './components/inline-edit-fields';
import Tooltip from '../tooltip/Tooltip';
import Button from '../button/button';
import ConfirmPopUp from '../confirm-modal/confirm-modal';
import useDeepMemo from '../../hooks/use-deep-memo';
import { PathnameDocument } from '../../constants/pathnames/pathname.document';
import { PathnameAccounting } from '../../constants/pathnames/pathname.accounting';
import { PathnameInventory } from '../../constants/pathnames/pathname.inventory';
import { PathnameManufacturing } from '../../constants/pathnames/pathname.manufacturing';
import { PathnamePurchase } from '../../constants/pathnames/pathname.procurement';
import { PathnameCrm } from '../../constants/pathnames/pathname.crm';
import { PathnameUsers } from '../../constants/pathnames/pathname.user';
import { PathnameRental } from '../../constants/pathnames/pathname.rental';
import { PathnameHrms } from '@/constants';

import './material-table.scss';

/* ============================================================================
 * Types
 * ==========================================================================*/

type ExtraEditVariants =
	| 'file'
	| 'image'
	| 'date'
	| 'number'
	| 'time'
	| 'date_time'
	| 'phone'
	| 'checkbox';

type DisableFunction = (row: any, virtualRow?: any) => boolean;

export type TypeBooleanLabels = {
	1: string;
	0: string;
	true: string;
	false: string;
};

export interface MaterialTableColumnProps
	extends Omit<MRT_ColumnDef<MRT_RowData>, 'editVariant'> {
	visible?: boolean;
	type?: string;
	required?: boolean;
	showDefaultCurrency?: boolean;
	valueGet?: string;
	custom_class?: string;
	/** when type is an array of objects */
	typeArrayAccessorKey?: string;
	/** when type is a boolean */
	typeBooleanLabels?: TypeBooleanLabels;
	editVariant?: MRT_ColumnDef<MRT_RowData>['editVariant'] | ExtraEditVariants;
	editProperties?: {
		field_name?: string;
		valueKey?: string;
		placeholder?: string;
		dateFormat?: string;
		validations?: any[];
		is_multiple?: boolean;
		is_multiline?: boolean;
		disable?: boolean | DisableFunction;
	};
	redirectionPathWithId?: string;
	handleRowClick?: (row: any) => void;
	idField?: string;
}

export interface MaterialTableColumnHeaderProps {
	column: MRT_ColumnDef<any, any>;
	header: MRT_Header<any>;
	table: MRT_TableInstance<any>;
}

type RowSelectionCallback = (row: MRT_Row<any>) => boolean;

export interface MaterialTablePropsType {
	rows: any[];
	columns: MaterialTableColumnProps[];
	totalPages: number;
	paginationModel: IPaginationModel;
	getSelectedData: any;

	enablePagination?: boolean;
	disableMaxHeight?: boolean;
  columnOrder?: string[];
	columnPinning?: MRT_ColumnPinningState;
	updateData?: (value: any, column: any, rowId: any) => void;
	handleColumnOrdering?: (colOrder: any) => void;
	enableEditing?: boolean | ((row: MRT_Row<any>) => boolean);
	enableColumnDragging?: boolean;
	rowActionMenu?: any[];
	destructiveActionMenu?: any[];
	getRowId?: (row: any, index: number) => string;
	states?: Partial<MRT_TableState<any>>;
	onSortingChange?: any;
	disableDefaultActionColumn?: boolean;
	enableRowSelection?: boolean | RowSelectionCallback;
	defaultActionColumnItems?: React.ReactElement[];
	enableBottomToolbar?: boolean;
	SetSelectedRowId?: (rows: MRT_Row<any>[]) => void;
	isResetRow?: boolean;
	onCreateRow?: (data: any) => Promise<unknown>;
	onEditRow?: (data: any, action?: string) => Promise<unknown>;
	onDeleteRow?: (data: any) => Promise<unknown>;
	editDisplayMode?: MaterialReactTableProps<any>['editDisplayMode'];
	enableRowCreate?: boolean;
	onChangeRowField?: (e: any, type: string, row: any) => void;
	onSelectRow?: (rows: any, prevSelected?: any) => any;
	onRowClick?: (row: MRT_Row<any>) => void;
	enableColumnResizing?: boolean;
	/**
	 * `onEnd` (default) commits the width once the drag finishes: no per-mousemove
	 * re-render of the whole table, which is the main source of visible jitter.
	 * Pass `onChange` to restore the previous live-resize behaviour.
	 */
	columnResizeMode?: 'onChange' | 'onEnd';
	selectedRowIds?: any[];
	onColumnVisibility?: (columns: MaterialTableColumnProps[]) => void;
	enableAddCustomField?: boolean;
	disableAddColumn?: boolean;
	disabledDefaultActionColumnIcon?: boolean;
	enableRowDragging?: boolean;
	handleColumnResizing?: (sizes: MRT_ColumnSizingState) => void;
	columnSizes?: MRT_ColumnSizingState;
	showPreview?: boolean;
	handlePreview?: (row: MRT_RowData) => void;
	/** Opaque background applied to every pinned header/body cell. */
	pinnedColumnBgColor?: string;
	defaultColumnProps?: Partial<MRT_ColumnDef<any>>;
	deleteConfirmTitle?: string;
	deleteConfirmDescription?: string;
	[key: string]: any;
}

/* ============================================================================
 * Module-level constants — hoisted so their identity never changes.
 * ==========================================================================*/

const EMPTY_ARRAY: any[] = [];
const EMPTY_OBJECT: any = Object.freeze({});
const DEFAULT_COLUMN_ORDER = ['mrt-row-select'];
const DEFAULT_PINNING = { left: ['actions', 'mrt-row-actions'] };
const SPAR_DECIMALS = 4;

const DEFAULT_COLUMN: Partial<MRT_ColumnDef<any>> = {
	// `minSize: 0` allowed columns to collapse to zero width mid-drag; 40 keeps
	// the grid layout stable without preventing narrow columns.
	minSize: 40,
	maxSize: 800
};

const DEFAULT_SIZING_INFO: MRT_ColumnSizingInfoState = {
	columnSizingStart: [],
	deltaOffset: null,
	deltaPercentage: null,
	isResizingColumn: false,
	startOffset: null,
	startSize: null
};

const CUSTOM_FORM_PATH_BY_MODULE: Record<string, string> = {
	accounting: PathnameAccounting.CUSTOM_FORM,
	inventory: PathnameInventory.CUSTOM_FORM,
	manufacturing: PathnameManufacturing.CUSTOM_FORM,
	procurement: PathnamePurchase.CUSTOM_FORM,
	crm: PathnameCrm.CUSTOM_FORM,
	user: PathnameUsers.CUSTOM_FORM,
	rental: PathnameRental.CUSTOM_FORM,
	drive: PathnameDocument.ADD_CUSTOM_FIELDS,
	hrms: PathnameHrms.CUSTOM_FORM
};

const DISPLAY_COLUMN_DEF_OPTIONS = {
	'mrt-row-expand': { size: 70 },
	'mrt-row-select': { size: 50 }
};

const HEAD_CELL_SX: Record<string, any> = {
	lineHeight: '140%',
	letterSpacing: '-0.28px',
	fontWeight: '500',
	fontSize: '0.875rem',
	'& .Mui-TableHeadCell-Content': { height: '100%' },
	'& .Mui-TableHeadCell-Content-Labels': {
		width: '100%',
		justifyContent: 'space-between'
	},
	'& .Mui-TableHeadCell-ResizeHandle-Wrapper': {
		padding: 0,
		margin: 0,
		right: '-5px',
		opacity: 0
	}
};

const BODY_CELL_SX: Record<string, any> = {
	lineHeight: '140%',
	letterSpacing: '-0.26px',
	fontWeight: '400',
	fontSize: '0.8125rem',
	// NOTE: this is intentionally NOT applied to pinned cells — a transparent
	// hover background is exactly what makes a sticky column see-through.
	'&:hover': { backgroundColor: 'transparent' }
};

/**
 * Opaque-background overrides for a pinned (sticky) cell.
 *
 * Rather than guessing at DOM markers (`.pinned-column`, `[data-pinned]`), which
 * differ between MRT majors, these are applied per-cell based on MRT's own
 * `column.getIsPinned()`. MRT merges consumer `sx` AFTER its internal pinned
 * styles, so this wins regardless of version.
 *
 * Four things have to be neutralised for a sticky cell to be truly opaque:
 *   1. `backgroundColor`  — MRT uses a semi-transparent `alpha(...)` value.
 *   2. `backgroundImage`  — MRT sets `inherit`, pulling in the Paper gradient.
 *   3. `::before`         — MRT v3 paints the pinned overlay here.
 *   4. `:hover`           — must not fall back to `transparent`.
 */
const buildPinnedCellSx = (bg: string) => ({
	backgroundColor: `${bg} !important`,
	backgroundImage: 'none !important',
	opacity: 1,
	'&::before': { backgroundColor: `${bg} !important`, opacity: 1 },
	'&:hover': { backgroundColor: `${bg} !important` }
});

const BODY_PROPS = { sx: { zIndex: 500 } } as const;
const HEAD_PROPS = { sx: { zIndex: 600 } } as const;
const BOTTOM_TOOLBAR_PROPS = {
	sx: { boxShadow: 'none', '& .MuiBox-root': { paddingLeft: 0 } }
} as const;
const CIRCULAR_PROGRESS_PROPS = {
	Component: null,
	sx: { color: '#289B64' }
} as const;
const CHECKBOX_SX = {
	color: 'theme.secondary.300',
	'&.Mui-checked': {
		color: 'theme.primary.600',
		'&:hover': { color: 'theme.primary.500' }
	},
	'&.Mui-disabled': { color: 'theme.secondary.200' }
} as const;
const ICON_SX = { color: 'theme.secondary.1000' } as const;
const PINNED_HEAD_PROPS = { className: 'pinned-column' };
const PINNED_BODY_PROPS = { className: 'pinned-column' };
// Column-level cell props replace the table-level ones wholesale for the same
// key, so the shared typography has to be merged back in here explicitly.
const PINNED_BODY_PROPS_WRAP = {
	className: 'pinned-column',
	sx: { ...BODY_CELL_SX, whiteSpace: 'pre-wrap' }
};

const TABLE_ICONS = {
	ArrowDownwardIcon: (props: SvgIconProps) => (
		<ArrowUpDown fontSize='medium' {...props} />
	),
	SortIcon: (props: SvgIconProps) => <ArrowUpDown fontSize='medium' {...props} />,
	SyncAltIcon: () => <ArrowUpDown fontSize='medium' />,
	CancelIcon: (props: SvgIconProps) => (
		<Close fontSize='small' {...props} sx={ICON_SX} />
	),
	SaveIcon: (props: SvgIconProps) => (
		<Save {...props} fontSize='small' sx={ICON_SX} />
	)
};

/* ============================================================================
 * Pure helpers — hoisted out of the component (they closed over nothing).
 * ==========================================================================*/

const shallowEqual = (a: Record<string, any> = {}, b: Record<string, any> = {}) => {
	if (a === b) return true;
	const aKeys = Object.keys(a);
	const bKeys = Object.keys(b);
	if (aKeys.length !== bKeys.length) return false;
	return aKeys.every((k) => a[k] === b[k]);
};

const getValidationType = (column: MaterialTableColumnProps) => {
	switch (column.editVariant) {
		case 'select':
			return column?.editProperties?.is_multiple ? yup.array() : yup.string();

		case 'number':
			return yup
				.number()
				.nullable()
				.transform((value, originalValue) =>
					originalValue === '' ? null : value
				);

		case 'url':
			return yup
				.string()
				.matches(
					/^(https?:\/\/)?((([a-zA-Z0-9_-]+\.)+[a-zA-Z]{2,})|localhost)(:\d{2,5})?(\/[a-zA-Z0-9#_-]+\/?)*(\?[a-zA-Z0-9&=_-]+)?(#\S+)?$/,
					{ excludeEmptyString: true, message: 'Please enter valid URL.' }
				);

		case 'date':
		case 'date_time':
		case 'time':
			return yup
				.date()
				.transform((value, originalValue) => {
					if (
						originalValue &&
						typeof originalValue === 'object' &&
						'toDate' in originalValue
					) {
						return (originalValue as any).toDate();
					}
					return value;
				})
				.typeError('Valid date is required');

		case 'file':
		case 'image':
			return yup.mixed().nullable();

		default:
			return yup.string();
	}
};

const validationReducer = (
	validations: any,
	validation: any,
	column: MaterialTableColumnProps
): any => {
	let vld = validations;

	switch (validation.type) {
		case 'required':
			vld = validation.value
				? validations.required(validation.msg_en).typeError(validation.msg_en)
				: column?.editProperties?.is_multiple
					? validations.min(1, validation.msg_en)
					: validations;
			break;
		case 'min':
			vld = validations.min(validation.value, validation.msg_en);
			break;
		case 'email':
			vld = validations.email(validation.value, validation.msg_en);
			break;
		case 'max':
			vld = validations.max(validation.value, validation.msg_en);
			break;
		case 'positive':
			vld =
				validation.value === true
					? validations.test(
							'is-positive-number',
							'Value must be a positive number',
							(value: any) => !value || value >= 0
						)
					: validations;
			break;
		case 'when':
			return validations.when(validation.relation, {
				is: (val: any) => val === validation.values,
				then: () =>
					validation?.thenValidations?.reduce(
						(acc: any, v: any) => validationReducer(acc, v, column),
						validations
					) || validations,
				otherwise: () =>
					validation?.otherWiseValidations?.reduce(
						(acc: any, v: any) => validationReducer(acc, v, column),
						validations
					) || validations
			});
		case 'validate':
			if (typeof validation.callback === 'function') {
				vld = vld.test(
					validation.name || 'custom-validate',
					validation.msg_en || 'Validation failed',
					function (this: yup.TestContext, value: any) {
						return validation.callback(value, this.parent);
					}
				);
			}
			break;
		default:
			vld = validations;
			break;
	}

	if (
		validation.type === 'required' &&
		validation.value &&
		(!column?.editVariant || column?.editVariant === 'text')
	) {
		vld = vld.test(
			'no-blank-spaces',
			'Blank space is not allowed',
			(value: any) => {
				if (!value) return true; // let .required() handle empty
				return String(value).trim().length > 0;
			}
		);
	}

	return vld;
};

const buildValidationSchema = (columns: MaterialTableColumnProps[]) => {
	const schema: Record<string, any> = {};
	columns.forEach((clm) => {
		if (clm?.editProperties?.validations?.length && clm.accessorKey) {
			schema[clm.accessorKey] = clm.editProperties.validations.reduce(
				(acc: any, validation: any) => validationReducer(acc, validation, clm),
				getValidationType(clm)
			);
		}
	});
	return yup.object().shape(schema);
};

const hasRequiredValidation = (editProperties?: MaterialTableColumnProps['editProperties']) =>
	Boolean(
		editProperties?.validations?.some((v: any) => v.type === 'required' && v.value !== false)
	);

const formatValues = (values: Record<string, any>, cells: any[]) => {
	const result: Record<string, any> = {};
	const cellByKey = new Map<string, any>(
		cells.map((c) => [c.accessorKey ?? c.id, c])
	);

	Object.keys(values).forEach((key) => {
		const val = values[key];
		result[key] = val;
		if (!val) return;

		const cell = cellByKey.get(key);
		if (val instanceof File) {
			result[key] = val.name;
		}
		if (cell?.editVariant === 'date') {
			result[key] = dayjs(val).format(
				cell?.editProperties?.dateFormat || cell?.dateFormat || 'DD-MM-YYYY'
			);
		}
	});

	return result;
};

const collectYupErrors = (err: yup.ValidationError) => {
	const errors: Record<string, string> = {};
	err.inner.forEach((error) => {
		if (error.path) errors[error.path] = error.message;
	});
	return errors;
};

/* ============================================================================
 * Hooks
 * ==========================================================================*/

/**
 * Returns a referentially stable function that always calls the latest `fn`.
 * Lets callers pass inline arrows without invalidating memoised column defs.
 */
function useEventCallback<T extends (...args: any[]) => any>(fn?: T) {
	const ref = useRef(fn);
	useLayoutEffect(() => {
		ref.current = fn;
	});
	return useCallback(
		(...args: Parameters<T>): ReturnType<T> | undefined => ref.current?.(...args),
		[]
	);
}

function useLatestRef<T>(value: T) {
	const ref = useRef(value);
	ref.current = value;
	return ref;
}

/* ============================================================================
 * Inline-edit context
 *
 * Validation errors change on every save attempt / keystroke-driven revalidation.
 * Passing them through context (instead of closing over them in the column defs)
 * keeps the memoised `columns` array stable while still re-rendering edit cells.
 * ==========================================================================*/

interface InlineEditContextValue {
	errors: Record<string, string>;
	validationSchema: yup.ObjectSchema<any>;
	setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
	onChangeRowField: (e: any, type: string, row: any) => void;
	getSelectedData: (name: any, row: any) => any;
}

const InlineEditContext = createContext<InlineEditContextValue | null>(null);

interface EditCellProps {
	col: MaterialTableColumnProps;
	cell: MRT_Cell<any>;
	column: any;
	row: MRT_Row<any>;
	table: MRT_TableInstance<any>;
}

const EditCell = memo(({ col, cell, column, row, table }: EditCellProps) => {
	const ctx = useContext(InlineEditContext);
	if (!ctx) return null;

	return (
		<InlineEditFields
			cell={cell}
			column={column}
			col={col}
			row={row}
			table={table}
			errors={ctx.errors}
			validationSchema={ctx.validationSchema}
			setValidationErrors={ctx.setValidationErrors}
			onChangeRowField={ctx.onChangeRowField}
			getSelectedData={ctx.getSelectedData}
		/>
	);
});
EditCell.displayName = 'EditCell';

/* ============================================================================
 * Small presentational pieces
 * ==========================================================================*/

const ColumnHeaderLabel = memo(
	({ label, required }: { label: React.ReactNode; required: boolean }) => (
		<Box display='flex' alignItems='center'>
			{label}
			{required && (
				<Typography type='s5' color='theme.error.600'>
					*
				</Typography>
			)}
		</Box>
	)
);
ColumnHeaderLabel.displayName = 'ColumnHeaderLabel';

const SparCurrencyCell = ({ cell }: { cell: MRT_Cell<any> }) => {
	const raw = cell.getValue<any>();
	if (raw === null || raw === undefined || raw === '') return '-';
	const num = Number(raw);
	return Number.isNaN(num) ? '-' : num.toFixed(SPAR_DECIMALS);
};

/**
 * Belt-and-braces CSS layer for pinned cells.
 *
 * The authoritative fix lives in `muiTableHeadCellProps` / `muiTableBodyCellProps`,
 * which key off `column.getIsPinned()`. These selectors additionally cover cells
 * that carry a pinned marker but are not pinned through MRT state (e.g. the
 * `preview` column, which ships the `.pinned-column` class), and any markup that
 * a future MRT major renders. `box-shadow` is deliberately left alone so MRT's
 * separator between the pinned block and the scrolling area survives.
 */
const PINNED_CELL_SELECTORS = [
	'.MuiTableCell-root[data-pinned="true"]',
	'.MuiTableCell-root.pinned-column',
	'th[data-pinned="true"]',
	'td[data-pinned="true"]'
].join(', ');

const TableWrapper = styled(Box, {
	shouldForwardProp: (prop) =>
		prop !== 'disableMaxHeight' && prop !== 'pinnedColumnBgColor'
})<{ disableMaxHeight?: boolean; pinnedColumnBgColor?: string }>(
	({ theme: { palette }, disableMaxHeight, pinnedColumnBgColor = '#fff' }) => ({
		[PINNED_CELL_SELECTORS]: {
			backgroundColor: `${pinnedColumnBgColor} !important`,
			backgroundImage: 'none !important',
			opacity: 1,
			'&::before': {
				backgroundColor: `${pinnedColumnBgColor} !important`,
				opacity: 1
			},
			'&:hover': { backgroundColor: `${pinnedColumnBgColor} !important` }
		},
		'.MuiTableCell-root': {
			border: '1px solid #efefef',
			borderBottom: 0,
			borderLeft: 0,
			padding: '0 0.5rem',
			lineHeight: '19px',
			fontSize: '14px',
			minHeight: 32
		},
		'.MuiTableCell-head': {
			backgroundColor: palette.theme?.secondary[100]
		},
		'.MuiTableContainer-root': {
			...(!disableMaxHeight && {
				maxHeight: 'clamp(350px, 100vh - 200px, 9999px) !important'
			})
		},
		'.MuiPaper-root': { boxShadow: 'none' },
		'.MuiPaginationItem-root': {
			'&.Mui-selected': {
				backgroundColor: palette?.theme?.primary[700],
				color: 'white'
			}
		},
		'.MuiTableRow-root': {
			'&.Mui-selected': {
				backgroundColor: `${palette?.theme?.primary[100]} !important`,
				color: 'white'
			}
		},
		'.MuiCheckbox-indeterminate': {
			color: `${palette?.theme?.primary[700]} !important`,
			'& svg': { height: '20px !important', width: '20px !important' }
		}
	})
);

/* ============================================================================
 * Component
 * ==========================================================================*/

const MaterialTableBase = ({
	rows,
	columns,
	totalPages,
	paginationModel,
	getSelectedData,

	enablePagination = false,
  columnOrder = DEFAULT_COLUMN_ORDER,
	columnPinning = DEFAULT_PINNING,
	updateData,
	handleColumnOrdering,
	enableEditing,
	enableColumnDragging,
	states,
	rowActionMenu,
	destructiveActionMenu,
	disableDefaultActionColumn,
	enableRowSelection,
	defaultActionColumnItems = EMPTY_ARRAY,
	enableBottomToolbar,
	SetSelectedRowId,
	isResetRow,
	onCreateRow,
	onEditRow,
	onDeleteRow,
	editDisplayMode,
	enableRowCreate,
	onChangeRowField,
	onSelectRow,
	onRowClick,
	enableColumnResizing = true,
	columnResizeMode = 'onEnd',
	selectedRowIds = EMPTY_ARRAY,
	onColumnVisibility,
	onSortingChange,
	disabledDefaultActionColumnIcon = false,
	enableAddCustomField = true,
	disableAddColumn = false,
	enableRowDragging = false,
	handleColumnResizing,
	columnSizes,
	disableMaxHeight = false,
	showPreview = false,
	handlePreview,
	pinnedColumnBgColor = '#fff',
	getRowId,
	defaultColumnProps,
	deleteConfirmTitle,
	deleteConfirmDescription,
	...rest
}: MaterialTablePropsType) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();

	const { formData = null, updatePageInfo, activePage: aPage } = usePages();
	const activePage = useDeepMemo<PageState>(aPage);
	const { pageNo } = paginationModel;

	/* ---------------------------------------------------------------- config */

	const isSpar = useMemo(() => getApiConfigSafe()?.countryEnv === 'spar', []);

	const customFieldPath = useMemo(() => {
		const currentModule = location.pathname.split('/')[2];
		return `/dashboard/${currentModule}${CUSTOM_FORM_PATH_BY_MODULE[currentModule] ?? ''}`;
	}, [location.pathname]);

	/* ------------------------------------------------------- stable callbacks */

	const onColumnVisibilityStable = useEventCallback(onColumnVisibility);
	const onChangeRowFieldStable = useEventCallback(onChangeRowField);
	const getSelectedDataStable = useEventCallback(getSelectedData);
	const handlePreviewStable = useEventCallback(handlePreview);
	const handleColumnResizingStable = useEventCallback(handleColumnResizing);
	const handleColumnOrderingStable = useEventCallback(handleColumnOrdering);
	const updateDataStable = useEventCallback(updateData);
	const onSelectRowStable = useEventCallback(onSelectRow);
	const setSelectedRowIdStable = useEventCallback(SetSelectedRowId);
	const onRowClickStable = useEventCallback(onRowClick);
	const updatePageInfoRef = useLatestRef(updatePageInfo);

	/* ------------------------------------------------------------------ state */

	/**
	 * Column visibility is stored as a sparse override map instead of a full
	 * mirror of the `columns` prop. This removes the props->state duplication
	 * (and the extra render it caused) while keeping fresh column defs — dynamic
	 * select options, Cell renderers etc. — always up to date.
	 */
	const [visibilityOverrides, setVisibilityOverrides] = useState<
		Record<string, boolean>
	>(EMPTY_OBJECT);
	const [columnSearch, setColumnSearch] = useState('');

	const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
	const [showAddColumnPanel, setShowAddColumnPanel] = useState(false);
	const [rowAnchorEl, setRowAnchorEl] = useState<null | HTMLElement>(null);
	const [activeMenuRow, setActiveMenuRow] = useState<any>(EMPTY_OBJECT);

	const [validationErrors, setValidationErrors] = useState<Record<string, string>>(
		EMPTY_OBJECT
	);
	const [isCreatingRow, setIsCreatingRow] = useState(false);
	const [isEditingRow, setIsEditingRow] = useState(false);
	const [isDeletingRow, setIsDeletingRow] = useState(false);
	const [rowToDelete, setRowToDelete] = useState<any>(null);
	const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>(
		EMPTY_OBJECT
	);
	const [sorting, setSorting] = useState<MRT_SortingState>(EMPTY_ARRAY);
	const [disableNextEdit, setDisableNextEdit] = useState(false);

	const [columnSizing, setColumnSizing] = useState<MRT_ColumnSizingState>(() => ({
		...(aPage?.column_sizes ?? {}),
		...(columnSizes ?? {})
	}));
	const [columnSizingInfo, setColumnSizingInfo] =
		useState<MRT_ColumnSizingInfoState>(DEFAULT_SIZING_INFO);

	/* --------------------------------------------------- derived column model */

	const resolvedColumns = useMemo<MaterialTableColumnProps[]>(
		() =>
			(columns ?? EMPTY_ARRAY).map((col) => {
				const key = (col.accessorKey ?? col.id) as string;
				const override = visibilityOverrides[key];
				if (override === undefined || override === Boolean(col.visible)) return col;
				return { ...col, visible: override };
			}),
		[columns, visibilityOverrides]
	);

	const visibleColumns = useMemo(
		() => resolvedColumns.filter((c) => Boolean(c.visible)),
		[resolvedColumns]
	);

	const hiddenColumns = useMemo(
		() => resolvedColumns.filter((c) => !c.visible),
		[resolvedColumns]
	);

	/** Replaces the `filteredOptions` state, which went stale on column changes. */
	const filteredHiddenColumns = useMemo(() => {
		const q = columnSearch.trim().toLowerCase();
		if (!q) return hiddenColumns;
		return hiddenColumns.filter((c) =>
			String(c.header ?? '').toLowerCase().includes(q)
		);
	}, [hiddenColumns, columnSearch]);

	const validationSchema = useMemo(
		() => buildValidationSchema(resolvedColumns),
		[resolvedColumns]
	);

	/* -------------------------------------------------------- menu handlers */

	const handleHeaderClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
		setMenuAnchorEl(event.currentTarget);
	}, []);

	const handleHeaderClose = useCallback(() => {
		setMenuAnchorEl(null);
		setShowAddColumnPanel(false);
		setColumnSearch('');
	}, []);

	const emitColumnVisibility = useCallback(
		(overrides: Record<string, boolean>) => {
			if (!onColumnVisibilityStable) return;
			const next = (columns ?? EMPTY_ARRAY).map((col) => {
				const key = (col.accessorKey ?? col.id) as string;
				const override = overrides[key];
				return override === undefined ? { ...col } : { ...col, visible: override };
			});
			onColumnVisibilityStable(next);
		},
		[columns, onColumnVisibilityStable]
	);

	const setColumnVisible = useCallback(
		(column: MaterialTableColumnProps, visible: boolean) => {
			const key = (column.accessorKey ?? column.id) as string;
			setVisibilityOverrides((prev) => {
				if (prev[key] === visible) return prev;
				const next = { ...prev, [key]: visible };
				emitColumnVisibility(next);
				return next;
			});
			setMenuAnchorEl(null);
			setShowAddColumnPanel(false);
			setColumnSearch('');
		},
		[emitColumnVisibility]
	);

	const handleHideColumn = useCallback(
		(column: MaterialTableColumnProps) => setColumnVisible(column, false),
		[setColumnVisible]
	);

	const handleAddColumn = useCallback(
		(column: MaterialTableColumnProps) => setColumnVisible(column, true),
		[setColumnVisible]
	);

	const handleCustomAddColumn = useCallback(() => {
		navigate(customFieldPath, { state: formData });
		setMenuAnchorEl(null);
		setShowAddColumnPanel(false);
	}, [navigate, customFieldPath, formData]);

	const handleActionsClick = useCallback(
		(event: React.MouseEvent<HTMLElement>, data: any) => {
			setActiveMenuRow(data);
			setRowAnchorEl(event.currentTarget);
		},
		[]
	);

	const handleActionsClose = useCallback(() => setRowAnchorEl(null), []);

	const handlePreviewClick = useCallback(
		(row: MRT_RowData) => handlePreviewStable(row),
		[handlePreviewStable]
	);

	/* ------------------------------------------------------------ row delete */

	const handleRowDelete = useCallback(async () => {
		if (!rowToDelete) return;
		setIsDeletingRow(true);
		try {
			await onDeleteRow?.(rowToDelete);
			setRowToDelete(null);
		} catch (err) {
			enqueueSnackbar(
				err instanceof Error ? err.message : 'Something went wrong',
				{ variant: 'error' }
			);
		} finally {
			setIsDeletingRow(false);
		}
	}, [rowToDelete, onDeleteRow]);

	const resetEditingState = useCallback(() => {
		setRowSelection(EMPTY_OBJECT);
		setDisableNextEdit(false);
		setValidationErrors(EMPTY_OBJECT);
	}, []);

	/* ------------------------------------------------------ save handlers */

	const handleSaveError = useCallback((err: unknown) => {
		if (err instanceof yup.ValidationError) {
			setValidationErrors(collectYupErrors(err));
		} else if (err instanceof Error) {
			enqueueSnackbar(err.message, { variant: 'error' });
		} else {
			enqueueSnackbar('Something went wrong', { variant: 'error' });
		}
	}, []);

	const handleEditingRowSave = useCallback(
		async (data: any) => {
			try {
				await validationSchema.validate(data.values, { abortEarly: false });
				setValidationErrors(EMPTY_OBJECT);
				setIsEditingRow(true);
				setDisableNextEdit(false);

				const cells = data.row.getAllCells().map((c: any) => c.column.columnDef);
				data.row._valuesCache = formatValues(data.values, cells);

				await onEditRow?.(data, 'edit');
				data.table.setEditingRow(null);
			} catch (err) {
				handleSaveError(err);
			} finally {
				setIsEditingRow(false);
			}
		},
		[validationSchema, onEditRow, handleSaveError]
	);

	const handleCreatingRowSave = useCallback(
		async (data: any) => {
			try {
				await validationSchema.validate(data.values, { abortEarly: false });
				setValidationErrors(EMPTY_OBJECT);
				setIsCreatingRow(true);
				setDisableNextEdit(false);

				const cells = data.row.getAllCells().map((c: any) => c.column.columnDef);
				data.row._valuesCache = formatValues(data.values, cells);

				await onCreateRow?.(data);
				data.table.setCreatingRow(null);
			} catch (err) {
				handleSaveError(err);
			} finally {
				setIsCreatingRow(false);
			}
		},
		[validationSchema, onCreateRow, handleSaveError]
	);

	/* ---------------------------------------------------------- column defs */

	const tableColumnDefs = useMemo(() => {
		const isRowEdit = editDisplayMode === 'row';

		const dataColumns = visibleColumns.map((c) => {
			const isCurrency = c.type === 'currency' || c.type === 'amount';

			return {
				...c,
				...(isSpar && isCurrency && !c.Cell ? { Cell: SparCurrencyCell } : {}),
				...(isRowEdit
					? {
							Edit: (props: any) => <EditCell {...props} col={c} />
						}
					: {}),
				Header: (
					<ColumnHeaderLabel
						label={c.header}
						required={Boolean(c.required) || hasRequiredValidation(c.editProperties)}
					/>
				),
				enableColumnDragging: c.id !== 'actions',
				size: undefined,
				maxSize: 800
			} as MRT_ColumnDef<any>;
		});

		const previewColumn = showPreview
			? [
					{
						id: 'preview',
						enableEditing: false,
						size: 70,
						header: (
							<IconButton
								size='small'
								sx={{ ml: '20px', '&:hover': { backgroundColor: 'transparent' } }}>
								<img src={images.common.eyeIcon} alt='Eye Icon' />
							</IconButton>
						),
						enableColumnDragging: false,
						visible: true,
						muiTableHeadCellProps: PINNED_HEAD_PROPS,
						muiTableBodyCellProps: PINNED_BODY_PROPS_WRAP,
						Cell: ({ row }: any) => (
							<Button
								size='small'
								variant='outlined'
								onClick={() => handlePreviewClick(row.original)}>
								{t('common.preview', { defaultValue: 'Preview' })}
							</Button>
						)
					} as unknown as MRT_ColumnDef<any>
				]
			: EMPTY_ARRAY;

		const hasAnyRowMenu =
			Boolean(rowActionMenu?.length) || Boolean(destructiveActionMenu?.length);

		const actionColumn = !disableDefaultActionColumn
			? [
					{
						id: 'actions',
						enableEditing: false,
						size: defaultActionColumnItems.length > 1 ? 80 : 50,
						header: (
							<IconButton
								size='small'
								sx={{ p: 0.5, '&:hover': { backgroundColor: 'transparent' } }}
								onClick={handleHeaderClick}>
								<img src={images.common.arrowIcon} alt='Arrow Icon' />
							</IconButton>
						),
						enableColumnDragging: false,
						visible: true,
						muiTableHeadCellProps: PINNED_HEAD_PROPS,
						muiTableBodyCellProps: PINNED_BODY_PROPS,
						Cell: (props: any) =>
							defaultActionColumnItems.length
								? defaultActionColumnItems.map((item, index) =>
										cloneElement(item, {
											...item.props,
											key: item.key ?? `default-action-${index}`,
											onClick: () => item.props?.onClick?.(props)
										})
									)
								: !disabledDefaultActionColumnIcon && (
										<IconButton
											onClick={(e) => handleActionsClick(e, props.row.original)}
											disabled={!hasAnyRowMenu}>
											<img src={images.more} alt='More Icon' />
										</IconButton>
									)
					} as unknown as MRT_ColumnDef<any>
				]
			: EMPTY_ARRAY;

		return [...dataColumns, ...previewColumn, ...actionColumn];
	}, [
		visibleColumns,
		isSpar,
		editDisplayMode,
		showPreview,
		handlePreviewClick,
		disableDefaultActionColumn,
		defaultActionColumnItems,
		disabledDefaultActionColumnIcon,
		rowActionMenu,
		destructiveActionMenu,
		handleHeaderClick,
		handleActionsClick,
		t
	]);

	/* --------------------------------------------------------- table options */

	const tableState = useMemo<Partial<MRT_TableState<any>>>(
		() => ({
			columnOrder,
			columnPinning,
			isSaving: isCreatingRow || isEditingRow,
			showProgressBars: false,
			rowSelection,
			...states,
			sorting: Array.isArray(states?.sorting)
				? states.sorting
				: Array.isArray(sorting)
					? sorting
					: EMPTY_ARRAY,
			columnSizing,
			showLoadingOverlay: false
		}),
		[columnOrder, isCreatingRow, isEditingRow, rowSelection, states, sorting, columnSizing, columnPinning]
	);

	const mergedDefaultColumn = useMemo(
		() => ({ ...DEFAULT_COLUMN, ...defaultColumnProps }),
		[defaultColumnProps]
	);

	const containerProps = useMemo(
		() => ({ sx: { ...(disableMaxHeight && { maxHeight: 'none !important' }) } }),
		[disableMaxHeight]
	);

	/**
	 * Four cached props objects (pinned / unpinned × head / body). The per-cell
	 * callbacks below only pick between these references, so making them
	 * functions costs nothing: no object is allocated during render or scroll.
	 */
	const cellPropVariants = useMemo(() => {
		const pinned = buildPinnedCellSx(pinnedColumnBgColor);
		return {
			head: { sx: HEAD_CELL_SX },
			headPinned: { sx: { ...HEAD_CELL_SX, ...pinned } },
			body: { sx: BODY_CELL_SX },
			bodyPinned: { sx: { ...BODY_CELL_SX, ...pinned } }
		};
	}, [pinnedColumnBgColor]);

	const muiTableHeadCellProps = useCallback(
		({ column }: any) =>
			column?.getIsPinned?.() ? cellPropVariants.headPinned : cellPropVariants.head,
		[cellPropVariants]
	);

	const muiTableBodyCellProps = useCallback(
		({ column }: any) =>
			column?.getIsPinned?.() ? cellPropVariants.bodyPinned : cellPropVariants.body,
		[cellPropVariants]
	);

	const paginationProps = useMemo(
		() => ({
			shape: 'rounded' as const,
			page: pageNo,
			count: totalPages,
			showRowsPerPage: false,
			boundaryCount: 3
		}),
		[pageNo, totalPages]
	);

	const selectCheckboxProps = useMemo(
		() => ({
			sx: CHECKBOX_SX,
			icon: <RecgtangleIcon sx={{ width: 20, height: 20 }} />,
			checkedIcon: <CheckBoxIcon sx={{ width: 20, height: 20 }} />
		}),
		[]
	);

	const selectAllCheckboxProps = useMemo(
		() => ({
			sx: { ...CHECKBOX_SX, '&.Mui-disabled': { color: 'theme.secondary.600' } },
			icon: <RecgtangleIcon sx={{ width: 20, height: 20 }} />,
			checkedIcon: <CheckBoxIcon sx={{ width: 20, height: 20 }} />
		}),
		[]
	);

	const displayColumnDefOptions = useMemo(
		() => ({
			...DISPLAY_COLUMN_DEF_OPTIONS,
			'mrt-row-actions': {
				Header: (
					<IconButton onClick={handleHeaderClick} size='small'>
						<img src={images.common.arrowIcon} alt='Arrow Icon' />
					</IconButton>
				),
				muiTableBodyCellProps: {
					sx: { '& .MuiBox-root': { gap: 0 }, backgroundColor: '#fff' }
				}
			}
		}),
		[handleHeaderClick]
	);

	const renderRowActions = useCallback(
		({ row, table }: { row: MRT_Row<any>; table: MRT_TableInstance<any> }) => {
			const startEditing = () => {
				setValidationErrors(EMPTY_OBJECT);
				table.setEditingRow(row);
				setDisableNextEdit(true);
			};

			const actions = (
				<Box sx={{ display: 'flex', gap: 0 }}>
					<Tooltip title={t('common.edit', { defaultValue: 'Edit' })}>
						<span>
							<IconButton disabled={disableNextEdit} onClick={startEditing} size='small'>
								<Edit fontSize='small' sx={ICON_SX} />
							</IconButton>
						</span>
					</Tooltip>
					{onDeleteRow && (
						<Tooltip title={t('common.delete', { defaultValue: 'Delete' })}>
							<span>
								<IconButton
									disabled={disableNextEdit}
									onClick={() => setRowToDelete(row)}
									size='small'>
									<Trash fontSize='small' sx={ICON_SX} />
								</IconButton>
							</span>
						</Tooltip>
					)}
				</Box>
			);

			if (rest?.enableExpanding) return row?.parentId ? actions : null;
			return actions;
		},
		[disableNextEdit, onDeleteRow, rest?.enableExpanding, t]
	);

	const renderBottomToolbarCustomActions = useCallback(
		({ table }: { table: MRT_TableInstance<any> }) => (
			<Button
				variant='outlined'
				startIcon={<Add />}
				className='add-row-btn'
				disabled={disableNextEdit}
				onClick={() => {
					setValidationErrors(EMPTY_OBJECT);
					table.setCreatingRow(true);
					setDisableNextEdit(true);
				}}>
				{t('common.add')}
			</Button>
		),
		[disableNextEdit, t]
	);

	const renderEmptyRowsFallback = useCallback(
		() => (
			<Box className='no-data'>
				<img src={images.common.tableNoData} alt='' />
				<Typography type='s3' weight='medium' color='theme.secondary.1000'>
					{t('common.noData')}
				</Typography>
			</Box>
		),
		[t]
	);

	const muiTableBodyRowProps = useCallback(
		({ row, table }: { row: MRT_Row<any>; table: MRT_TableInstance<any> }) => {
			const tState = table.getState();
			const isRowBeingEdited = Boolean(tState?.editingRow?.id || tState?.creatingRow?.id);
			const isErrorRow = Boolean(row?.original?.has_error);

			return {
				...(onRowClick ? { onClick: () => onRowClickStable(row) } : {}),
				sx: {
					cursor: 'pointer',
					backgroundColor: !isRowBeingEdited
						? isErrorRow
							? 'theme.error.100'
							: 'inherit'
						: 'inherit',
					'&:hover': {
						backgroundColor: isRowBeingEdited
							? 'transparent'
							: isErrorRow
								? 'theme.error.200'
								: '#e8e8e8'
					},
					'&:hover td:after': { backgroundColor: 'transparent' }
				}
			};
		},
		[onRowClick, onRowClickStable]
	);

	const muiEditTextFieldProps = useCallback(
		({ row, column }: any) => ({
			InputProps: { disableUnderline: true },
			onBlur: (e: React.FocusEvent<HTMLInputElement>) =>
				updateDataStable(e.target.value, column, row.id),
			variant: 'outlined' as const,
			sx: ({ palette }: any) => ({
				'& .MuiOutlinedInput-root': {
					'& fieldset': {
						borderColor: `${palette.theme?.primary[700]} !important`
					}
				}
			})
		}),
		[updateDataStable]
	);

	const resolvedGetRowId = useCallback(
		(row: any, index: number) =>
			getRowId ? getRowId(row, index) : (row?.id ?? `row-${index}`),
		[getRowId]
	);

	const table = useMaterialReactTable({
		columns: tableColumnDefs,
		data: rows ?? EMPTY_ARRAY,
		defaultColumn: mergedDefaultColumn,
		state: tableState,
		getRowId: resolvedGetRowId,

		muiTableContainerProps: containerProps,
		columnResizeMode,

		onEditingRowSave: handleEditingRowSave,
		onCreatingRowSave: handleCreatingRowSave,
		onCreatingRowCancel: resetEditingState,
		onEditingRowCancel: resetEditingState,
		onSortingChange: states?.sorting ? onSortingChange : setSorting,
		onRowSelectionChange: setRowSelection,
		onColumnOrderChange: handleColumnOrderingStable,
		onColumnSizingChange: setColumnSizing,
		onColumnSizingInfoChange: setColumnSizingInfo,

		displayColumnDefOptions,
		renderRowActions,
		renderEmptyRowsFallback,
		...(enableRowCreate ? { renderBottomToolbarCustomActions } : {}),

		enableColumnOrdering: true,
		enableRowSelection: enableRowSelection ?? true,
		enableEditing: enableEditing ?? true,
		editDisplayMode: editDisplayMode ?? 'cell',
		createDisplayMode: 'row',
		enableColumnResizing,
		enableRowDragging,
		enableTopToolbar: false,
		enableColumnActions: false,
		enableSorting: true,
		enableGrouping: true,
		enableBottomToolbar: enableBottomToolbar ?? false,
		enableColumnDragging: enableColumnDragging ?? true,
		enableStickyHeader: true,
		enableSortingRemoval: false,
		enablePagination,
		paginationDisplayMode: 'pages',
		layoutMode: 'grid',

		muiPaginationProps: paginationProps,
		muiEditTextFieldProps,
		muiCircularProgressProps: CIRCULAR_PROGRESS_PROPS,
		muiTableHeadCellProps,
		muiTableBodyProps: BODY_PROPS,
		muiTableHeadProps: HEAD_PROPS,
		muiTableBodyCellProps,
		muiTableBodyRowProps,
		muiSelectCheckboxProps: selectCheckboxProps,
		muiSelectAllCheckboxProps: selectAllCheckboxProps,
		muiBottomToolbarProps: BOTTOM_TOOLBAR_PROPS,
		icons: TABLE_ICONS,

		...rest
	});

	const tableRef = useLatestRef(table);

	/* ------------------------------------------------------------- effects */

	/**
	 * Persist column widths on the *resize-end edge only*.
	 * Previously this ran on every `columnSizing` change (i.e. every mousemove
	 * in `onChange` mode) and on mount, pushing a page-state update that
	 * re-rendered the whole page tree — the primary "fluctuation" source.
	 */
	const columnSizingRef = useLatestRef(columnSizing);
	const wasResizingRef = useRef(false);

	useEffect(() => {
		const isResizing = Boolean(columnSizingInfo?.isResizingColumn);
		const wasResizing = wasResizingRef.current;
		wasResizingRef.current = isResizing;

		if (!wasResizing || isResizing) return;

		const previous = activePage?.column_sizes ?? EMPTY_OBJECT;
		const merged = { ...previous, ...columnSizingRef.current };
		if (shallowEqual(previous, merged)) return;

		updatePageInfoRef.current?.({ column_sizes: merged });
		handleColumnResizingStable(columnSizingRef.current);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [columnSizingInfo?.isResizingColumn]);

	/** Keep the controlled `columnSizes` prop in sync without clobbering drags. */
	useEffect(() => {
		if (!columnSizes) return;
		setColumnSizing((prev) => {
			const next = { ...prev, ...columnSizes };
			return shallowEqual(prev, next) ? prev : next;
		});
	}, [columnSizes]);

	/** Notify the parent about sorting — skipping the mount pass. */
	const isFirstSortRun = useRef(true);
	useEffect(() => {
		if (isFirstSortRun.current) {
			isFirstSortRun.current = false;
			return;
		}
		if (typeof onSortingChange === 'function') {
			onSortingChange(sorting?.[0] ?? null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sorting]);

	/**
	 * Hydrate selection from `selectedRowIds`. Applied once per distinct id set
	 * (instead of the old one-shot latch), so later external changes are honoured
	 * without clobbering the user's in-table selection on every data refresh.
	 */
	const selectedIdsKey = useMemo(
		() => selectedRowIds.map(String).sort().join('|'),
		[selectedRowIds]
	);
	const appliedSelectionKeyRef = useRef<string | null>(null);

	useEffect(() => {
		if (!selectedIdsKey || !rows?.length) return;
		if (appliedSelectionKeyRef.current === selectedIdsKey) return;

		const idSet = new Set(selectedRowIds.map(String));
		const next: MRT_RowSelectionState = {};
		rows.forEach((row) => {
			if (idSet.has(String(row?.id))) next[row.id] = true;
		});
		if (!Object.keys(next).length) return;

		appliedSelectionKeyRef.current = selectedIdsKey;
		setRowSelection((prev) => (shallowEqual(prev, next) ? prev : next));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedIdsKey, rows]);

	/**
	 * Emit selection changes. Depends on `rowSelection` only — the old version
	 * also depended on `table`, whose identity changes every render, which fired
	 * the parent callback on every render and caused feedback re-render loops.
	 */
	useEffect(() => {
		const instance = tableRef.current;
		if (!instance) return;

		if (enableRowSelection) {
			setSelectedRowIdStable(instance.getSelectedRowModel().rows);
		}

		if (!onSelectRow) return;

		if (rest?.enableExpanding) {
			const selected: any[] = [];
			const selectionState = instance.getState().rowSelection;
			const traverse = (rowList: any[], parentKey = '') => {
				rowList.forEach((row, index) => {
					const rowKey = parentKey ? `${parentKey}.${index}` : `${row.id}`;
					if (selectionState[rowKey]) selected.push(row);
					if (row.subRows?.length) traverse(row.subRows, rowKey);
				});
			};
			traverse(instance.getRowModel().rows);
			onSelectRowStable(selected, rowSelection);
		} else {
			onSelectRowStable(instance.getSelectedRowModel().rows, rowSelection);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rowSelection, enableRowSelection]);

	/** External reset. */
	useEffect(() => {
		if (isResetRow) {
			table?.resetRowSelection();
			appliedSelectionKeyRef.current = null;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isResetRow]);

	/* ------------------------------------------------------ context value */

	const inlineEditContextValue = useMemo<InlineEditContextValue>(
		() => ({
			errors: validationErrors,
			validationSchema,
			setValidationErrors,
			onChangeRowField: onChangeRowFieldStable,
			getSelectedData: getSelectedDataStable
		}),
		[validationErrors, validationSchema, onChangeRowFieldStable, getSelectedDataStable]
	);

	/* ------------------------------------------------------------- render */

	return (
		<InlineEditContext.Provider value={inlineEditContextValue}>
			<TableWrapper
				className='MRT-TableWrapper custom-table'
				disableMaxHeight={disableMaxHeight}
				pinnedColumnBgColor={pinnedColumnBgColor}>
				<MaterialReactTable table={table} />

				<Menu
					anchorEl={menuAnchorEl}
					open={Boolean(menuAnchorEl)}
					onClose={handleHeaderClose}
					className='add-role-drp'>
					{showAddColumnPanel ? (
						<div>
							<MenuItem
								onClick={() => setShowAddColumnPanel(false)}
								className='role-drp-list add-column-title'>
								<ListItemIcon className='add-icon'>
									<ChevronLeftIcon />
								</ListItemIcon>
								<ListItemText className='title'>
									{t('common.addColumn_label')}
								</ListItemText>
							</MenuItem>
							<Divider
								flexItem
								sx={{ mt: '0.25rem !important', mb: '0.25rem !important' }}
							/>
							<div className='searchbar-box-list'>
								<SearchBar
									placeholder={t('common.search', { defaultValue: 'Search' })}
									handleSearch={(value: any) =>
										setColumnSearch(
											typeof value === 'string' ? value : (value?.target?.value ?? '')
										)
									}
								/>
							</div>
							<div className='all-attribute-title'>
								<Typography variant='body2' color='text.secondary'>
									{t('common.allAttributes_label')}
								</Typography>
							</div>
							{filteredHiddenColumns.map((column) => (
								<MenuItem
									key={String(column.accessorKey ?? column.id)}
									onClick={() => handleAddColumn(column)}>
									<Typography color='theme.secondary.1000' type='s3'>
										{column.header}
									</Typography>
								</MenuItem>
							))}
							<Divider
								flexItem
								sx={{ mt: '0.25rem !important', mb: '0.25rem !important' }}
							/>
							{enableAddCustomField && (
								<MenuItem onClick={handleCustomAddColumn} className='role-drp-list'>
									<ListItemIcon className='add-icon'>
										<img src={images.common.addIcon} alt='Add Icon' />
									</ListItemIcon>
									<ListItemText className='title'>
										{t('common.addCustomField_label')}
									</ListItemText>
								</MenuItem>
							)}
						</div>
					) : (
						<>
							{visibleColumns.map((column) => (
								<MenuItem
									key={String(column.accessorKey ?? column.id)}
									onClick={() => handleHideColumn(column)}
									className='role-drp-list'>
									<ListItemIcon className='add-icon'>
										<img src={images.common.dotsIcon} alt='Drag Icon' />
									</ListItemIcon>
									<ListItemText className='title'>{column.header}</ListItemText>
									<ListItemIcon className='eye-icon'>
										<img src={images.common.eyeIcon} alt='Eye Icon' />
									</ListItemIcon>
								</MenuItem>
							))}
							<Divider
								flexItem
								sx={{ mt: '0.25rem !important', mb: '0.25rem !important' }}
							/>
							{!disableAddColumn && (
								<MenuItem
									onClick={() => setShowAddColumnPanel(true)}
									className='role-drp-list'>
									<ListItemIcon className='add-icon'>
										<img src={images.common.addIcon} alt='Add Icon' />
									</ListItemIcon>
									<ListItemText className='title'>
										{t('common.addColumn_label')}
									</ListItemText>
									<Typography
										variant='body2'
										color='text.secondary'
										className='eye-icon'>
										<ChevronRightIcon sx={{ fontSize: '1.25rem !important' }} />
									</Typography>
								</MenuItem>
							)}
						</>
					)}
				</Menu>

				<Menu
					anchorEl={rowAnchorEl}
					open={Boolean(rowAnchorEl)}
					onClose={handleActionsClose}>
					{rowActionMenu?.map((option: any, index: number) => (
						<MenuItem
							key={option.key ?? `row-action-${index}`}
							onClick={async () => {
								await option.handleAction(activeMenuRow);
								setRowAnchorEl(null);
							}}
							disabled={
								typeof option.disabled === 'function'
									? option.disabled(activeMenuRow)
									: Boolean(option.disabled)
							}
							sx={{ alignItems: 'center', gap: 0.5 }}>
							{option?.icon}
							<Typography type='s4' color='inherit'>
								{typeof option.label === 'function'
									? option.label(activeMenuRow)
									: option.label}
							</Typography>
						</MenuItem>
					))}

					{Boolean(destructiveActionMenu?.length) && [
						<Divider
							key='destructive-divider'
							flexItem
							sx={{ mt: '0.25rem !important', mb: '0.25rem !important' }}
						/>,
						<div
							key='destructive-actions'
							className='dropdown-inner--DestructiveHeader'>
							{destructiveActionMenu?.map((option: any, index: number) => (
								<MenuItem
									key={option.key ?? `destructive-action-${index}`}
									onClick={() => {
										option.handleAction(activeMenuRow);
										setRowAnchorEl(null);
									}}
									className='dropdown-inner--DestructiveActions'
									disabled={
										typeof option.disabled === 'function'
											? option.disabled(activeMenuRow)
											: Boolean(option.disabled)
									}>
									<Typography type='s4' color='inherit'>
										{typeof option.label === 'function'
											? option.label(activeMenuRow)
											: option.label}
									</Typography>
								</MenuItem>
							))}
						</div>
					]}
				</Menu>

				<ConfirmPopUp
					open={Boolean(rowToDelete)}
					onClose={() => setRowToDelete(null)}
					onConfirm={handleRowDelete}
					title={deleteConfirmTitle ?? t('common.deleteItem', { defaultValue: 'Delete Item' })}
					description={
						deleteConfirmDescription ??
						t('common.deleteConfirm', {
							defaultValue: 'Are you sure you want to delete entry ?'
						})
					}
					loading={isDeletingRow}
				/>
			</TableWrapper>
		</InlineEditContext.Provider>
	);
};

export const MaterialTable = memo(MaterialTableBase);
MaterialTable.displayName = 'MaterialTable';

export default MaterialTable;
